import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Alert,
  Button,
  Tabs,
  Tab,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Business as BusinessIcon } from '@mui/icons-material';
import { apiClient } from '../../services/apiClient';
import { LoadingScreen } from '../../components/common/LoadingScreen';
import OrganizationMembersTab from '../../components/admin/OrganizationMembersTab';
import OrganizationEmailDomainsTab from '../../components/admin/OrganizationEmailDomainsTab';
import OrganizationLocationsTab from '../../components/admin/OrganizationLocationsTab';
import OrganizationLocationGroupsTab from '../../components/admin/OrganizationLocationGroupsTab';
import OrganizationFeatureFlagsTab from '../../components/admin/OrganizationFeatureFlagsTab';

interface Country {
  code: string;
  name: string;
}

interface Organization {
  id: number;
  name: string;
  owner_uuid: string;
  primary_country_code: string;
  created_at: string;
  updated_at: string;
  primary_country: Country;
  owner_name: string;
  owner_email: string;
}

export default function OrganizationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    loadOrganization();
  }, [id]);

  const loadOrganization = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.get<Organization>(`/admin/organizations/${id}`);
      setOrganization(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load organisation');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingScreen message="Loading organisation..." />;
  }

  if (error || !organization) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 2 }}>
          <Alert severity="error">
            {error || 'Organisation not found'}
          </Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/organizations')}
            sx={{ mt: 2 }}
          >
            Back to Organisations
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/organizations')}
            sx={{ mb: 2 }}
          >
            Back to Organisations
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <BusinessIcon sx={{ fontSize: { xs: 24, sm: 32 }, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                {organization.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                {organization.primary_country?.name} &middot; Owner: {organization.owner_name} ({organization.owner_email})
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
            <Tab label="Members" />
            <Tab label="Email Domains" />
            <Tab label="Locations" />
            <Tab label="Location Groups" />
            <Tab label="Feature Flags" />
          </Tabs>
        </Box>

        {/* Tab Content */}
        {activeTab === 0 && <OrganizationMembersTab organizationId={organization.id} />}
        {activeTab === 1 && <OrganizationEmailDomainsTab organizationId={organization.id} />}
        {activeTab === 2 && <OrganizationLocationsTab organizationId={organization.id} />}
        {activeTab === 3 && <OrganizationLocationGroupsTab organizationId={organization.id} />}
        {activeTab === 4 && <OrganizationFeatureFlagsTab organizationId={organization.id} />}
      </Box>
    </Container>
  );
}
